/**
 * ENS (ENS_ES) Score Calculation
 * ENS = 50% Self-Assessment + 50% Controls
 * Controls: IMPLEMENTED=100, PARTIAL=50, NOT_IMPLEMENTED=0, N_A=excluded
 */

export type EnsControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';

const STATUS_TO_PERCENT: Record<EnsControlStatus, number | null> = {
  IMPLEMENTED: 100,
  PARTIAL: 50,
  NOT_IMPLEMENTED: 0,
  N_A: null,
};

function normalizeStatus(s: string | undefined | null): EnsControlStatus | null {
  if (!s || typeof s !== 'string') return null;
  const u = s.toUpperCase().replace(/\s+/g, '_');
  if (u === 'IMPLEMENTED' || u === 'PARTIAL' || u === 'NOT_IMPLEMENTED' || u === 'N_A' || u === 'NA') {
    return (u === 'NA' ? 'N_A' : u) as EnsControlStatus;
  }
  return null;
}

/**
 * Compute ENS Controls score from control answers.
 */
export function computeEnsControlsScore(controlsAnswers: Record<string, string>): {
  score: number;
  breakdown: { implemented: number; partial: number; notImplemented: number; na: number; totalNonNA: number };
} {
  const breakdown = { implemented: 0, partial: 0, notImplemented: 0, na: 0, totalNonNA: 0 };
  let sum = 0;

  for (const raw of Object.values(controlsAnswers)) {
    const status = normalizeStatus(raw);
    if (!status) continue;

    if (status === 'N_A') {
      breakdown.na++;
      continue;
    }

    breakdown.totalNonNA++;
    const pct = STATUS_TO_PERCENT[status];
    if (pct !== null) sum += pct;

    if (status === 'IMPLEMENTED') breakdown.implemented++;
    else if (status === 'PARTIAL') breakdown.partial++;
    else breakdown.notImplemented++;
  }

  const score = breakdown.totalNonNA > 0
    ? Math.round((sum / (breakdown.totalNonNA * 100)) * 100)
    : 0;

  return { score, breakdown };
}

/**
 * Compute ENS total score: 50% Self-Assessment + 50% Controls
 */
export function computeEnsScore(params: {
  selfAssessmentScore: number | null | undefined;
  controlsScore: number | null | undefined;
}): number {
  const sa = typeof params.selfAssessmentScore === 'number' && Number.isFinite(params.selfAssessmentScore)
    ? Math.max(0, Math.min(100, params.selfAssessmentScore))
    : 0;
  const ctrl = typeof params.controlsScore === 'number' && Number.isFinite(params.controlsScore)
    ? Math.max(0, Math.min(100, params.controlsScore))
    : 0;

  if (sa === 0 && ctrl === 0) return 0;
  return Math.round(0.5 * sa + 0.5 * ctrl);
}
